import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  Camera, Upload, Image, Settings, LogOut, User, Building2, 
  Users, TrendingUp, Package, Link as LinkIcon, Trash2, Plus,
  Download, Share2, ChevronRight, AlertCircle, Facebook, Instagram, MessageCircle,
  DollarSign, BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Loader2, Crown, Sparkles,
  Menu, X, Check, Clock, CheckCircle, XCircle, Eye, ExternalLink, ArrowRight
} from 'lucide-react';
import Footer from '../components/Footer';
import useVirtualTryOn, { SIZES, FIT_OPTIONS } from '../hooks/useVirtualTryOn';
import { NotificationInbox } from '../components/NotificationInbox';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, isSubscribed, refreshUser, loading: authLoading } = useAuth();
  const { counts: notificationCounts, setOnCountsChange } = useNotifications();
  const [activeTab, setActiveTab] = useState('tryon');
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Store link and Instagram link state for influencers
  const [storeLink, setStoreLink] = useState('');
  const [instagramLink, setInstagramLink] = useState('');
  const [savingLinks, setSavingLinks] = useState(false);
  const [linksSaved, setLinksSaved] = useState(false);
  
  // Collaboration Content state (for both brands and influencers)
  const [collabContent, setCollabContent] = useState({ submissions: [], counts: {} });
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [campaignApplications, setCampaignApplications] = useState([]);
  const [collabContentLoading, setCollabContentLoading] = useState(false);
  const [reviewingContent, setReviewingContent] = useState(null);
  const [reviewFeedback, setReviewFeedback] = useState('');
  
  // Affiliate Network state
  
  // Campaign & Wallet state
  const [campaigns, setCampaigns] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    product_name: '',
    product_url: '',
    fee_per_content: 50,
    max_influencers: 5,
    requirements: ''
  });
  const [depositAmount, setDepositAmount] = useState(100);
  const [myApplications, setMyApplications] = useState([]);
  const [applyingToCampaign, setApplyingToCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [processingApplication, setProcessingApplication] = useState(null);
  const [showSubmitContentModal, setShowSubmitContentModal] = useState(null);
  const [contentSubmission, setContentSubmission] = useState({ image_base64: '', caption: '' });
  
  // Stripe Connect state (for influencers)
  const [connectStatus, setConnectStatus] = useState(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(50);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null);

  // Check if user is on Pro/Enterprise plan (should not see upgrade buttons)
  const isProPlan = user?.plan_id === 'brand_enterprise' || user?.plan_id === 'influencer_pro';

  // Virtual Try-On (shared hook)
  const tryon = useVirtualTryOn({
    mode: 'authenticated',
    token,
    onComplete: () => refreshUser(),
  });

  const {
    userPhoto, userPhotoPreview, clothesImage, clothesPreview,
    generatedImage, generating, progress: generatingProgress, error,
    size, fitPreference, setSize, setFitPreference,
    handleImageUpload: handleFileUpload, handleGenerate, handleDownload,
    handleShare, reset: handleReset, setError,
  } = tryon;

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Wait for auth to finish loading before redirecting
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
    } else if (!isSubscribed) {
      if (user.plan_id) {
        navigate('/checkout?plan=' + user.plan_id);
      } else {
        const typeParam = user.user_type === 'brand' ? 'brand' : 
                         user.user_type === 'influencer' ? 'influencer' : 'personal';
        navigate('/subscribe?type=' + typeParam);
      }
    }
  }, [user, isSubscribed, navigate, authLoading]);

  // Fetch unread message count for brands and influencers
  const fetchUnreadCount = useCallback(async () => {
    if (!token || !user || (user.user_type !== 'brand' && user.user_type !== 'influencer')) return;
    
    try {
      const chatRes = await fetch(`${API_URL}/api/chat/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (chatRes.ok) {
        const data = await chatRes.json();
        setUnreadMessages(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [API_URL, token, user]);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!token || !user || (user.user_type !== 'brand' && user.user_type !== 'influencer')) return;
    
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/user/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalyticsData(data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [API_URL, token, user]);

  useEffect(() => {
    if (user?.user_type === 'brand' || user?.user_type === 'influencer') {
      fetchUnreadCount();
      // Poll for new messages every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  // Fetch analytics when analytics tab is active
  useEffect(() => {
    if (activeTab === 'analytics' && (user?.user_type === 'brand' || user?.user_type === 'influencer')) {
      fetchAnalytics();
    }
  }, [activeTab, user, fetchAnalytics]);

  // Load store link and Instagram link for influencers
  useEffect(() => {
    if (user?.user_type === 'influencer') {
      // Use nullish check — empty string means user cleared the field
      setStoreLink(user.store_link ?? user.affiliate_link ?? '');
      setInstagramLink(user.instagram_link ?? '');
    }
  }, [user]);

  // Save influencer links
  const saveInfluencerLinks = async () => {
    if (!token) return;
    
    setSavingLinks(true);
    setLinksSaved(false);
    
    // Auto-prepend https:// if user entered a bare URL
    const normalizeUrl = (url) => {
      if (!url || url.trim() === '') return '';
      const trimmed = url.trim();
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
      return `https://${trimmed}`;
    };
    
    const normalizedStore = normalizeUrl(storeLink);
    const normalizedInstagram = normalizeUrl(instagramLink);
    
    try {
      const res = await fetch(`${API_URL}/api/influencer/links`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          store_link: normalizedStore,
          instagram_link: normalizedInstagram
        })
      });
      
      if (res.ok) {
        setStoreLink(normalizedStore);
        setInstagramLink(normalizedInstagram);
        setLinksSaved(true);
        // Refresh user data to get updated links
        if (refreshUser) refreshUser();
        // Reset saved indicator after 3 seconds
        setTimeout(() => setLinksSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving links:', err);
    } finally {
      setSavingLinks(false);
    }
  };

  useEffect(() => {
    if (user?.user_type === 'brand') {
      fetchPortfolio();
    }
  }, [user]);

  const fetchPortfolio = async () => {
    try {
      const res = await fetch(`${API_URL}/api/portfolio`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPortfolio(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching portfolio:', err);
    }
  };
  
  // Fetch collaboration content submissions (for influencers)
  const fetchMySubmissions = useCallback(async () => {
    if (!token || user?.user_type !== 'influencer') return;
    
    setCollabContentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/collaboration-content/my-submissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCollabContent(data);
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
    } finally {
      setCollabContentLoading(false);
    }
  }, [API_URL, token, user?.user_type]);
  
  // Fetch pending approvals (for brands)
  const fetchPendingApprovals = useCallback(async () => {
    if (!token || user?.user_type !== 'brand') return;
    
    setCollabContentLoading(true);
    try {
      const [contentRes, appsRes] = await Promise.all([
        fetch(`${API_URL}/api/collaboration-content/pending-approval`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/campaigns/pending-applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (contentRes.ok) {
        const data = await contentRes.json();
        setPendingApprovals(data.pending_content || []);
      }
      if (appsRes.ok) {
        const data = await appsRes.json();
        setCampaignApplications(data.applications || []);
      }
    } catch (err) {
      console.error('Error fetching pending approvals:', err);
    } finally {
      setCollabContentLoading(false);
    }
  }, [API_URL, token, user?.user_type]);
  
  // Review content (approve/reject)
  const handleReviewContent = async (contentId, action) => {
    if (!token) return;
    
    setReviewingContent(contentId);
    try {
      const res = await fetch(`${API_URL}/api/collaboration-content/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content_id: contentId,
          action: action,
          feedback: action === 'reject' ? reviewFeedback : ''
        })
      });
      
      if (res.ok) {
        // Refresh the pending list
        fetchPendingApprovals();
        setReviewFeedback('');
      }
    } catch (err) {
      console.error('Error reviewing content:', err);
    } finally {
      setReviewingContent(null);
    }
  };
  
  // Fetch collab content when relevant tab is active
  useEffect(() => {
    if (activeTab === 'collabs' && user?.user_type === 'influencer') {
      fetchMySubmissions();
    }
    if (activeTab === 'collabs' && user?.user_type === 'brand') {
      fetchPendingApprovals();
    }
  }, [activeTab, user?.user_type, fetchMySubmissions, fetchPendingApprovals]);
  
  // Fetch pending approvals count for brands on mount (for badge)
  useEffect(() => {
    if (user?.user_type === 'brand' && token) {
      fetchPendingApprovals();
    }
  }, [user?.user_type, token, fetchPendingApprovals]);
  
  // Campaign & Wallet Functions
  const fetchWallet = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/wallet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWalletData(data);
      }
    } catch (err) {
      console.error('Error fetching wallet:', err);
    }
  }, [token]);
  
  const fetchCampaigns = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  }, [token]);
  
  const fetchMyApplications = useCallback(async () => {
    if (!token || user?.user_type !== 'influencer') return;
    try {
      const res = await fetch(`${API_URL}/api/campaigns/my-applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyApplications(data.applications || []);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    }
  }, [token, user?.user_type]);
  
  // Live notification-driven data refresh
  // NOTE: Must be after fetchCampaigns and fetchMyApplications definitions
  useEffect(() => {
    if (!setOnCountsChange) return;
    setOnCountsChange((newCounts) => {
      // Refresh relevant data when notification counts change
      if (user?.user_type === 'brand') {
        fetchPendingApprovals();
        fetchCampaigns();
      }
      if (user?.user_type === 'influencer') {
        fetchMyApplications();
      }
    });
    return () => setOnCountsChange(null);
  }, [setOnCountsChange, user?.user_type, fetchPendingApprovals, fetchCampaigns, fetchMyApplications]);
  
  const depositToWallet = async () => {
    if (!token || depositAmount < 10) return;
    setProcessingPayment(true);
    try {
      const res = await fetch(`${API_URL}/api/wallet/deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          amount: depositAmount,
          origin_url: window.location.origin
        })
      });
      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const data = await res.json();
        setPaymentMessage({ type: 'error', text: data.detail || 'Failed to create payment session' });
      }
    } catch (err) {
      console.error('Error creating deposit session:', err);
      setPaymentMessage({ type: 'error', text: 'Failed to process payment' });
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Check deposit status after returning from Stripe
  const checkDepositStatus = async (sessionId) => {
    if (!token || !sessionId) return;
    setProcessingPayment(true);
    setPaymentMessage({ type: 'pending', text: 'Verifying payment...' });
    
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/wallet/deposit/status/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') {
            setPaymentMessage({ type: 'success', text: `Deposit successful! $${data.amount} added to your wallet.` });
            fetchWallet();
            setProcessingPayment(false);
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          } else if (data.status === 'expired') {
            setPaymentMessage({ type: 'error', text: 'Payment session expired. Please try again.' });
            setProcessingPayment(false);
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentMessage({ type: 'error', text: 'Payment verification timed out. Please check your wallet.' });
          setProcessingPayment(false);
        }
      } catch (err) {
        console.error('Error checking deposit status:', err);
        setProcessingPayment(false);
      }
    };
    
    poll();
  };
  
  // Fetch Stripe Connect account status (for influencers)
  const fetchConnectStatus = useCallback(async () => {
    if (!token || user?.user_type !== 'influencer') return;
    try {
      const res = await fetch(`${API_URL}/api/connect/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConnectStatus(data);
      }
    } catch (err) {
      console.error('Error fetching connect status:', err);
    }
  }, [token, user?.user_type]);
  
  // Start Stripe Connect onboarding
  const startConnectOnboarding = async () => {
    if (!token) return;
    setProcessingPayment(true);
    try {
      const res = await fetch(`${API_URL}/api/connect/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.onboarding_url;
      } else {
        const data = await res.json();
        setPaymentMessage({ type: 'error', text: data.detail || 'Failed to start onboarding' });
      }
    } catch (err) {
      console.error('Error starting connect onboarding:', err);
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Open Stripe Connect dashboard
  const openConnectDashboard = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/connect/dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        window.open(data.dashboard_url, '_blank');
      }
    } catch (err) {
      console.error('Error opening dashboard:', err);
    }
  };
  
  // Request withdrawal (influencer)
  const requestWithdrawal = async () => {
    if (!token || withdrawAmount < 10) return;
    setProcessingPayment(true);
    try {
      const res = await fetch(`${API_URL}/api/connect/withdraw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount: withdrawAmount })
      });
      const data = await res.json();
      if (res.ok) {
        setPaymentMessage({ type: 'success', text: data.message });
        setShowWithdrawModal(false);
        setWithdrawAmount(50);
        fetchWallet();
      } else {
        setPaymentMessage({ type: 'error', text: data.detail || 'Withdrawal failed' });
      }
    } catch (err) {
      console.error('Error requesting withdrawal:', err);
      setPaymentMessage({ type: 'error', text: 'Failed to process withdrawal' });
    } finally {
      setProcessingPayment(false);
    }
  };
  
  const createCampaign = async () => {
    if (!token || !campaignForm.name || !campaignForm.product_name) return;
    try {
      const res = await fetch(`${API_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(campaignForm)
      });
      if (res.ok) {
        setShowCreateCampaignModal(false);
        setCampaignForm({
          name: '',
          product_name: '',
          product_url: '',
          fee_per_content: 50,
          max_influencers: 5,
          requirements: ''
        });
        fetchCampaigns();
        fetchWallet();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to create campaign');
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
    }
  };
  
  const applyToCampaign = async (campaignId) => {
    if (!token) return;
    setApplyingToCampaign(campaignId);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/apply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchCampaigns();
        fetchMyApplications();
      }
    } catch (err) {
      console.error('Error applying:', err);
    } finally {
      setApplyingToCampaign(null);
    }
  };
  
  // Fetch single campaign with applications (for brands)
  const fetchCampaignDetails = async (campaignId) => {
    if (!token) return;
    setCampaignLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedCampaign(data);
      }
    } catch (err) {
      console.error('Error fetching campaign:', err);
    } finally {
      setCampaignLoading(false);
    }
  };
  
  // Accept application
  const acceptApplication = async (campaignId, applicationId) => {
    if (!token) return;
    setProcessingApplication(applicationId);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/accept/${applicationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchCampaignDetails(campaignId);
        fetchCampaigns();
      }
    } catch (err) {
      console.error('Error accepting:', err);
    } finally {
      setProcessingApplication(null);
    }
  };
  
  // Reject application
  const rejectApplication = async (campaignId, applicationId) => {
    if (!token) return;
    setProcessingApplication(applicationId);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/reject/${applicationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchCampaignDetails(campaignId);
      }
    } catch (err) {
      console.error('Error rejecting:', err);
    } finally {
      setProcessingApplication(null);
    }
  };
  
  // Submit content for campaign (influencer)
  const submitCampaignContent = async (campaignId, applicationData) => {
    if (!token || !contentSubmission.image_base64) return;
    setCampaignLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/submit-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_base64: contentSubmission.image_base64,
          product_name: applicationData?.product_name || 'Campaign Content',
          caption: contentSubmission.caption
        })
      });
      if (res.ok) {
        setShowSubmitContentModal(null);
        setContentSubmission({ image_base64: '', caption: '' });
        fetchMyApplications();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to submit content');
      }
    } catch (err) {
      console.error('Error submitting content:', err);
    } finally {
      setCampaignLoading(false);
    }
  };
  
  // Handle content image upload for campaign submission
  const handleContentImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large. Maximum size is 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      setContentSubmission(prev => ({ ...prev, image_base64: base64 }));
    };
    reader.readAsDataURL(file);
  };
  
  // Fetch campaigns data when campaigns tab is active
  useEffect(() => {
    if (activeTab === 'campaigns') {
      fetchWallet();
      fetchCampaigns();
      if (user?.user_type === 'influencer') {
        fetchMyApplications();
        fetchConnectStatus();
      }
    }
  }, [activeTab, user?.user_type, fetchWallet, fetchCampaigns, fetchMyApplications, fetchConnectStatus]);
  
  // Check for Stripe return (deposit success or connect complete)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const deposit = urlParams.get('deposit');
    const connect = urlParams.get('connect');
    const tabParam = urlParams.get('tab');
    
    // Deep link to specific tab from notifications
    if (tabParam) {
      const validTabs = ['tryon', 'portfolio', 'links', 'collabs', 'campaigns', 'analytics'];
      if (validTabs.includes(tabParam)) {
        setActiveTab(tabParam);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (sessionId && deposit === 'success') {
      checkDepositStatus(sessionId);
    } else if (deposit === 'cancelled') {
      setPaymentMessage({ type: 'error', text: 'Payment cancelled' });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (connect === 'complete') {
      setPaymentMessage({ type: 'success', text: 'Bank account connected successfully!' });
      fetchConnectStatus();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (connect === 'refresh') {
      // User needs to restart onboarding
      setPaymentMessage({ type: 'pending', text: 'Please complete your account setup to receive payouts.' });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 flex flex-col">
      {/* Navigation - Same as Landing Page */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 cursor-default">
              <Sparkles className="w-8 h-8 text-rose-400" />
              <span className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
                VirtualFit
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/dashboard" className="text-rose-500 font-medium transition-colors">Dashboard</Link>
              <Link to="/search" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Discover Brands & Influencers</Link>
              <Link to="/blog" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Blog</Link>
              <Link to="/about" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">About</Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right mr-2">
                <p className="text-sm font-medium text-gray-800">{user.name}</p>
                <p className="text-xs text-gray-500">
                  {user.images_used || 0} / {user.images_limit || 0} images
                </p>
              </div>
              <NotificationInbox />
              <Link to="/settings" className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-white/50" title="Account Settings">
                <User className="w-5 h-5" />
              </Link>
              <button onClick={logout} className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-white/50" title="Logout">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-gray-600 hover:text-rose-500" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col space-y-4">
                {/* User Info */}
                <div className="pb-3 border-b border-gray-100">
                  <p className="font-medium text-gray-800">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.images_used || 0} / {user.images_limit || 0} images</p>
                </div>
                
                {/* Navigation Links */}
                <Link 
                  to="/dashboard" 
                  className="text-rose-500 font-medium" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/search" 
                  className="text-gray-700 hover:text-rose-500 font-medium" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Discover Brands & Influencers
                </Link>
                <Link 
                  to="/blog" 
                  className="text-gray-700 hover:text-rose-500 font-medium" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link 
                  to="/about" 
                  className="text-gray-700 hover:text-rose-500 font-medium" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                
                {/* Divider */}
                <div className="border-t border-gray-200 pt-3">
                  <Link 
                    to="/notifications" 
                    className="block text-gray-700 hover:text-rose-500 font-medium mb-3" 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Notifications
                  </Link>
                  <Link 
                    to="/settings" 
                    className="flex items-center text-gray-700 hover:text-rose-500 font-medium" 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </Link>
                </div>
                <button 
                  onClick={() => { logout(); setMobileMenuOpen(false); }} 
                  className="flex items-center text-left text-gray-700 hover:text-rose-500 font-medium"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content with top padding for fixed nav */}
      <div className="pt-20 flex-1">
        <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Usage Warning */}
        {user.images_used >= user.images_limit * 0.8 && (
          <div className={`rounded-xl p-4 mb-6 flex items-start ${
            user.images_used >= user.images_limit
              ? 'bg-red-50 border border-red-200'
              : 'bg-amber-50 border border-amber-200'
          }`} data-testid="usage-warning-banner">
            <AlertCircle className={`w-5 h-5 mr-3 flex-shrink-0 ${
              user.images_used >= user.images_limit ? 'text-red-600' : 'text-amber-600'
            }`} />
            <div className="flex-1">
              <p className={`font-medium ${user.images_used >= user.images_limit ? 'text-red-800' : 'text-amber-800'}`}>
                {user.images_used >= user.images_limit ? 'Image limit reached' : 'Running low on images'}
              </p>
              <p className={`text-sm ${user.images_used >= user.images_limit ? 'text-red-700' : 'text-amber-700'}`}>
                You've used {user.images_used} of {user.images_limit} images this month.
                {user.images_used >= user.images_limit && ' Upgrade your plan to continue generating try-ons.'}
              </p>
              {user.images_used >= user.images_limit && !isProPlan && user.user_type !== 'personal' && (
                <Link
                  to="/subscription"
                  className="inline-flex items-center mt-2 px-4 py-1.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all"
                  data-testid="usage-upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Tabs - Responsive for mobile */}
        <div className="flex items-center justify-between sm:justify-start sm:space-x-4 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('tryon')}
            className={`pb-3 sm:pb-4 px-2 sm:px-4 font-medium transition-colors whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial ${
              activeTab === 'tryon' 
                ? 'text-rose-500 border-b-2 border-rose-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Camera className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
            <span>Try On</span>
          </button>
          
          {user.user_type === 'brand' && (
            <button
              onClick={() => setActiveTab('portfolio')}
              className={`pb-3 sm:pb-4 px-2 sm:px-4 font-medium transition-colors whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial ${
                activeTab === 'portfolio' 
                  ? 'text-rose-500 border-b-2 border-rose-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Package className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
              <span>Portfolio</span>
            </button>
          )}
          
          {user.user_type === 'influencer' && (
            <>
              <Link
                to={`/influencer/${user.handle}`}
                className="pb-3 sm:pb-4 px-2 sm:px-4 font-medium text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial"
                data-testid="influencer-portfolio-link"
              >
                <Package className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
                <span>Portfolio</span>
              </Link>
              <button
                onClick={() => setActiveTab('links')}
                className={`pb-3 sm:pb-4 px-2 sm:px-4 font-medium transition-colors whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial ${
                  activeTab === 'links' 
                    ? 'text-rose-500 border-b-2 border-rose-500' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <LinkIcon className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
                <span>My Links</span>
              </button>
            </>
          )}
          
          {(user.user_type === 'brand' || user.user_type === 'influencer') && (
            <Link
              to="/collaborations"
              className="pb-3 sm:pb-4 px-2 sm:px-4 font-medium text-gray-500 hover:text-gray-700 transition-colors relative whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial"
              data-testid="collaborations-link"
            >
              <div className="relative">
                <Users className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
                {(unreadMessages + (notificationCounts?.pending_collab_invites || 0)) > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="unread-badge">
                    {(unreadMessages + (notificationCounts?.pending_collab_invites || 0)) > 99 ? '99+' : (unreadMessages + (notificationCounts?.pending_collab_invites || 0))}
                  </span>
                )}
              </div>
              <span>Collabs</span>
            </Link>
          )}
          
          {/* Content Tab - For Brand approvals and Influencer submissions */}
          {(user.user_type === 'brand' || user.user_type === 'influencer') && (
            <button
              onClick={() => setActiveTab('collabs')}
              className={`pb-3 sm:pb-4 px-2 sm:px-4 font-medium transition-colors whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial relative ${
                activeTab === 'collabs' 
                  ? 'text-rose-500 border-b-2 border-rose-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="content-tab"
            >
              <div className="relative">
                <Eye className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
                {user.user_type === 'brand' && (pendingApprovals.length + campaignApplications.length) > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {pendingApprovals.length + campaignApplications.length}
                  </span>
                )}
              </div>
              <span>Content</span>
            </button>
          )}
          
          {/* Campaigns Tab */}
          {(user.user_type === 'brand' || user.user_type === 'influencer') && (
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`pb-3 sm:pb-4 px-2 sm:px-4 font-medium transition-colors whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial ${
                activeTab === 'campaigns' 
                  ? 'text-rose-500 border-b-2 border-rose-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="campaigns-tab"
            >
              <div className="relative">
                <DollarSign className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
                {user.user_type === 'brand' && (notificationCounts?.pending_applications || 0) > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {notificationCounts.pending_applications}
                  </span>
                )}
              </div>
              <span>Campaigns</span>
            </button>
          )}
          
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-3 sm:pb-4 px-2 sm:px-4 font-medium transition-colors whitespace-nowrap flex flex-col sm:flex-row items-center text-xs sm:text-base flex-1 sm:flex-initial ${
              activeTab === 'analytics' 
                ? 'text-rose-500 border-b-2 border-rose-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <TrendingUp className="w-5 h-5 sm:mr-2 mb-1 sm:mb-0" />
            <span>Analytics</span>
          </button>
        </div>

        {/* Try On Tab */}
        {activeTab === 'tryon' && (
          <div>
            {!generatedImage ? (
              <>
              {/* Size & Fit Preferences */}
              <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
                <h3 className="font-semibold text-gray-800 mb-4">Size & Fit Preferences</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Size Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                    <div className="flex flex-wrap gap-2">
                      {SIZES.map((s) => (
                        <button
                          key={s}
                          onClick={() => setSize(s)}
                          className={`px-4 py-2 rounded-lg font-medium transition-all ${
                            size === s
                              ? 'bg-rose-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`size-${s}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Fit Preference */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fit Preference</label>
                    <div className="flex flex-wrap gap-2">
                      {FIT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setFitPreference(option.value)}
                          className={`px-3 py-2 rounded-lg transition-all ${
                            fitPreference === option.value
                              ? 'bg-rose-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`fit-${option.value}`}
                        >
                          <div className="font-medium text-sm">{option.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Age & Policy Notice */}
                <div className="md:col-span-2 text-center text-xs text-gray-400">
                  You must be at least 18 to use this service. By uploading, you agree to our <a href="/acceptable-use" className="text-rose-400 hover:underline">Acceptable Use</a> and <a href="/biometric-policy" className="text-rose-400 hover:underline">Biometric Data</a> policies.
                </div>
                {/* User Photo Upload */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Your Photo
                  </h3>
                  
                  <label className="block">
                    <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      userPhotoPreview ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-rose-300'
                    }`}>
                      {userPhotoPreview ? (
                        <img src={userPhotoPreview} alt="Your photo" className="max-h-64 mx-auto rounded-lg" />
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">Upload your photo</p>
                          <p className="text-sm text-gray-400 mt-1">Full-body, front-facing on a plain background</p>
                          <p className="text-xs text-gray-300 mt-1">PNG, JPG up to 10MB</p>
                        </>
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'user')} className="hidden" />
                  </label>
                </div>

                {/* Clothes Upload */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Image className="w-5 h-5 mr-2" />
                    Clothing
                  </h3>
                  
                  <label className="block">
                    <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      clothesPreview ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-rose-300'
                    }`}>
                      {clothesPreview ? (
                        <img src={clothesPreview} alt="Clothing" className="max-h-64 mx-auto rounded-lg" />
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">Upload clothing image</p>
                          <p className="text-sm text-gray-400 mt-1">Single item on a white or plain background</p>
                          <p className="text-xs text-gray-300 mt-1">PNG, JPG up to 10MB</p>
                        </>
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'clothes')} className="hidden" />
                  </label>
                </div>
              </div>

              {error && (
                <div className="mt-6 rounded-xl p-4 text-center" data-testid="tryon-error-display">
                  {(error.toLowerCase().includes('limit') || error.toLowerCase().includes('upgrade') || error.toLowerCase().includes('subscription')) ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-amber-800 font-semibold mb-1">Limit Reached</p>
                      <p className="text-amber-700 text-sm mb-3">{error}</p>
                      {!isProPlan && user.user_type !== 'personal' && (
                        <Link
                          to="/subscription"
                          className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium hover:shadow-lg transition-all text-sm"
                          data-testid="upgrade-plan-btn"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade Plan
                        </Link>
                      )}
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-red-700">{error}</p>
                    </div>
                  )}
                </div>
              )}

              {(userPhoto || clothesImage) && (
                <div className="mt-8 text-center">
                  {user.images_used >= user.images_limit ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 inline-block" data-testid="tryon-limit-block">
                      <p className="text-amber-800 font-semibold mb-1">Image limit reached</p>
                      <p className="text-amber-700 text-sm mb-3">
                        You've used all {user.images_limit} images this month.
                        {user.user_type !== 'personal' ? ' Upgrade your plan to continue.' : ' Your limit resets next month.'}
                      </p>
                      {!isProPlan && user.user_type !== 'personal' && (
                        <Link
                          to="/subscription"
                          className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium hover:shadow-lg transition-all text-sm"
                          data-testid="tryon-limit-upgrade-btn"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade Plan
                        </Link>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerate}
                      disabled={generating || !userPhoto || !clothesImage}
                      className="px-8 py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="generate-tryon-btn"
                    >
                      {generating ? (generatingProgress || 'Generating...') : 'Generate Try-On'}
                    </button>
                  )}
                </div>
              )}
              </>
            ) : (
              /* Result */
              <div className="bg-white rounded-2xl p-6 shadow-sm max-w-2xl mx-auto">
                <h3 className="font-semibold text-gray-800 mb-4 text-center">Your Virtual Try-On</h3>
                <img 
                  src={generatedImage.startsWith('http') ? generatedImage : (generatedImage.startsWith('data:') ? generatedImage : `data:image/png;base64,${generatedImage}`)} 
                  alt="Generated" 
                  className="max-h-[500px] mx-auto rounded-xl"
                />
                
                {/* AI Disclaimer */}
                <p className="text-xs text-gray-400 text-center mt-3">This is an AI-generated image. Actual fit, color, and texture may differ from the real product.</p>
                
                {/* Download & Share Buttons */}
                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
                  <button
                    onClick={handleDownload}
                    className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                    data-testid="download-btn"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
                  >
                    Try Another
                  </button>
                </div>
                
                {/* Social Share */}
                <div className="border-t mt-6 pt-6">
                  <p className="text-center text-gray-600 mb-4">Share your look</p>
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={() => handleShare('twitter')}
                      className="p-3 bg-black text-white rounded-full hover:opacity-90 transition-opacity"
                      title="Share on X"
                      data-testid="share-x"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="p-3 bg-[#4267B2] text-white rounded-full hover:opacity-90 transition-opacity"
                      title="Share on Facebook"
                      data-testid="share-facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleShare('instagram')}
                      className="p-3 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white rounded-full hover:opacity-90 transition-opacity"
                      title="Share on Instagram"
                      data-testid="share-instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Portfolio Tab (Brands) */}
        {activeTab === 'portfolio' && user.user_type === 'brand' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Your Clothing Portfolio</h2>
              <Link 
                to="/portfolio"
                className="flex items-center px-4 py-2 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-lg font-medium hover:shadow-lg transition-all"
                data-testid="dashboard-add-item-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Item
              </Link>
            </div>
            
            {portfolio.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No items yet</h3>
                <p className="text-gray-600 mb-4">Upload your clothing items to let customers try them virtually</p>
                <Link 
                  to="/portfolio"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Your First Item
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {portfolio.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <img 
                      src={item.image_url || item.cloudinary_url || (item.image_base64 ? `data:image/png;base64,${item.image_base64}` : '/placeholder-clothing.png')} 
                      alt={item.name}
                      className="w-full aspect-[3/4] object-contain bg-gray-50"
                    />
                    <div className="p-3 sm:p-4">
                      <h4 className="font-medium text-gray-800 text-sm sm:text-base truncate">{item.name}</h4>
                      {item.price && <p className="text-rose-500 font-semibold text-sm sm:text-base">${item.price}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Link to full portfolio management */}
            <div className="mt-6 text-center">
              <Link 
                to="/portfolio" 
                className="text-rose-500 hover:text-rose-600 font-medium"
              >
                Manage Full Portfolio →
              </Link>
            </div>
          </div>
        )}

        {/* My Links Tab (Influencers) */}
        {activeTab === 'links' && user.user_type === 'influencer' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Store / Affiliate Link</h3>
              <p className="text-sm text-gray-600 mb-4">
                Add your store, affiliate, or shopping link. This will appear on your public profile.
              </p>
              <input
                type="text"
                value={storeLink}
                onChange={(e) => setStoreLink(e.target.value)}
                placeholder="amazon.com/shop/yourstore or your affiliate link"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                data-testid="store-link-input"
              />
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Instagram Profile</h3>
              <p className="text-sm text-gray-600 mb-4">
                Add your Instagram profile link so brands and followers can find you.
              </p>
              <input
                type="text"
                value={instagramLink}
                onChange={(e) => setInstagramLink(e.target.value)}
                placeholder="instagram.com/yourusername"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                data-testid="instagram-link-input"
              />
            </div>
            
            <div className="flex items-center gap-3 mb-6">
              <button 
                onClick={saveInfluencerLinks}
                disabled={savingLinks}
                className="px-6 py-2 bg-rose-500 text-white rounded-lg font-medium hover:bg-rose-600 disabled:opacity-50 flex items-center gap-2"
                data-testid="save-links-btn"
              >
                {savingLinks ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : linksSaved ? (
                  <Check className="w-4 h-4" />
                ) : null}
                {linksSaved ? 'Saved!' : 'Save Links'}
              </button>
              <a 
                href={`/influencer/${user.handle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-rose-500 hover:text-rose-600"
              >
                View on profile →
              </a>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">How Earnings Work</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-rose-100 text-rose-500 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <p><strong>Paid Campaigns:</strong> Join brand campaigns and earn fixed payments for creating virtual try-on content</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-rose-100 text-rose-500 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <p><strong>Store/Affiliate Link:</strong> Add your affiliate link and earn commission when followers shop through it</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-rose-100 text-rose-500 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <p><strong>Grow Your Audience:</strong> Share your Instagram profile to build your following</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4">Profile Stats</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-gray-800">{user.profile_views || 0}</p>
                  <p className="text-sm text-gray-500">Profile Visits</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-purple-500">{user.total_clicks || 0}</p>
                  <p className="text-sm text-gray-500">Link Clicks</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-rose-500">{user.collaborations_count || 0}</p>
                  <p className="text-sm text-gray-500">Active Collabs</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Tab - Collaboration Content Management */}
        {activeTab === 'collabs' && (
          <div className="space-y-6" data-testid="content-section">
            {collabContentLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
              </div>
            ) : user.user_type === 'brand' ? (
              /* Brand View - Campaign Applications + Pending Approvals */
              <div>
                {/* Campaign Applications Section */}
                {campaignApplications.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-gray-800">
                        Campaign Applications
                        <span className="ml-2 px-2 py-1 bg-rose-100 text-rose-700 text-sm rounded-full">
                          {campaignApplications.length}
                        </span>
                      </h2>
                    </div>
                    <div className="space-y-3">
                      {campaignApplications.map((app, idx) => (
                        <div key={`${app.campaign_id}-${app.influencer_id}-${idx}`} className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-rose-400" data-testid={`campaign-app-${idx}`}>
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-100 to-orange-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {app.influencer_image ? (
                                <img src={app.influencer_image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Users className="w-6 h-6 text-rose-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-800">
                                {app.influencer_name} <span className="text-gray-400 font-normal">@{app.influencer_handle}</span>
                              </p>
                              <p className="text-sm text-gray-500">
                                Applied to <span className="font-medium text-rose-600">{app.campaign_name}</span>
                              </p>
                              {app.message && (
                                <p className="text-sm text-gray-500 mt-1 line-clamp-2 italic">"{app.message}"</p>
                              )}
                              {app.applied_at && (
                                <p className="text-xs text-gray-400 mt-1">{new Date(app.applied_at).toLocaleDateString()}</p>
                              )}
                            </div>
                            <button
                              onClick={() => { setActiveTab('campaigns'); }}
                              className="px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-sm rounded-lg hover:shadow-lg flex-shrink-0"
                              data-testid={`review-app-${idx}`}
                            >
                              Review
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content Pending Approval Section */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800">
                    Content Pending Approval
                    {pendingApprovals.length > 0 && (
                      <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                        {pendingApprovals.length}
                      </span>
                    )}
                  </h2>
                </div>
                
                {pendingApprovals.length === 0 && campaignApplications.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">All Caught Up!</h3>
                    <p className="text-gray-500">No campaign applications or content waiting for your approval.</p>
                  </div>
                ) : pendingApprovals.length === 0 ? (
                  <div className="bg-white rounded-2xl p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-gray-600">No Content Pending</h3>
                    <p className="text-sm text-gray-400">No content submissions waiting for your review.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingApprovals.map(content => (
                      <div key={content.id} className="bg-white rounded-2xl p-6 shadow-sm" data-testid={`pending-content-${content.id}`}>
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Content Image - Full size, clickable */}
                          <div className="w-full md:w-72 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                            <img 
                              src={content.image_base64 ? `data:image/jpeg;base64,${content.image_base64}` : content.image_url || '/placeholder.png'}
                              alt="Collaboration content"
                              className="w-full h-auto object-contain cursor-pointer"
                              style={{ maxHeight: 480 }}
                              onClick={() => window.open(content.image_base64 ? `data:image/jpeg;base64,${content.image_base64}` : content.image_url, '_blank')}
                              title="Click to view full image"
                            />
                          </div>
                          
                          {/* Content Details */}
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-semibold text-gray-800">
                                  From @{content.influencer_handle}
                                </h4>
                                <p className="text-sm text-gray-500">{content.influencer_name}</p>
                              </div>
                              <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </span>
                            </div>
                            
                            <div className="mb-3">
                              <p className="text-sm text-gray-600 break-all">
                                <span className="font-medium">Product:</span> {content.product_name}
                              </p>
                              {content.caption && (
                                <p className="text-sm text-gray-600 mt-1 break-all line-clamp-3">
                                  <span className="font-medium">Caption:</span> {content.caption}
                                </p>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-400 mb-4">
                              Submitted {new Date(content.submitted_at).toLocaleDateString()}
                            </p>
                            
                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-3">
                              <button
                                onClick={() => handleReviewContent(content.id, 'approve')}
                                disabled={reviewingContent === content.id}
                                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                                data-testid={`approve-btn-${content.id}`}
                              >
                                {reviewingContent === content.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Approve
                              </button>
                              
                              <div className="flex-1 flex items-center gap-2">
                                <input
                                  type="text"
                                  placeholder="Feedback (optional)"
                                  value={reviewFeedback}
                                  onChange={(e) => setReviewFeedback(e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                                <button
                                  onClick={() => handleReviewContent(content.id, 'reject')}
                                  disabled={reviewingContent === content.id}
                                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                                  data-testid={`reject-btn-${content.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Reject
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <strong>How it works:</strong> When influencers create content featuring your products, 
                    they submit it here for your approval. Approved content will appear on both your profile 
                    and the influencer's profile as "Collaboration Content."
                  </p>
                </div>
              </div>
            ) : user.user_type === 'influencer' ? (
              /* Influencer View - My Submissions */
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800">My Collaboration Submissions</h2>
                  <div className="flex gap-2 text-sm">
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full flex items-center">
                      <Clock className="w-3 h-3 mr-1" /> {collabContent.counts?.pending || 0} Pending
                    </span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1" /> {collabContent.counts?.approved || 0} Approved
                    </span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full flex items-center">
                      <XCircle className="w-3 h-3 mr-1" /> {collabContent.counts?.rejected || 0} Rejected
                    </span>
                  </div>
                </div>
                
                {(!collabContent.submissions || collabContent.submissions.length === 0) ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Submissions Yet</h3>
                    <p className="text-gray-500 mb-4">
                      When you add a look and tag a brand collaboration, it will appear here.
                    </p>
                    <Link 
                      to={`/influencer/${user.handle}`}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add a Look
                    </Link>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {collabContent.submissions.map(submission => (
                      <div 
                        key={submission.id} 
                        className="bg-white rounded-xl overflow-hidden shadow-sm"
                        data-testid={`submission-${submission.id}`}
                      >
                        <div className="relative bg-gray-50">
                          <img 
                            src={submission.image_base64 ? `data:image/jpeg;base64,${submission.image_base64}` : submission.image_url || '/placeholder.png'}
                            alt="Submission"
                            className="w-full max-h-[480px] object-contain"
                          />
                          <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium flex items-center ${
                            submission.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            submission.status === 'approved' ? 'bg-green-100 text-green-700' :
                            submission.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {submission.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {submission.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {submission.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                            {!submission.status && <CheckCircle className="w-3 h-3 mr-1" />}
                            {submission.status ? (submission.status.charAt(0).toUpperCase() + submission.status.slice(1)) : 'Legacy'}
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-gray-800">@{submission.brand_handle}</p>
                            <p className="text-sm text-gray-500">{submission.brand_name}</p>
                          </div>
                          <p className="text-sm text-gray-600 mb-1 break-all">
                            <span className="font-medium">Product:</span> {submission.product_name}
                          </p>
                          {submission.caption && (
                            <p className="text-sm text-gray-500 line-clamp-2 break-all">{submission.caption}</p>
                          )}
                          
                          {submission.status === 'rejected' && submission.reviewer_feedback && (
                            <div className="mt-3 p-2 bg-red-50 rounded-lg">
                              <p className="text-xs text-red-600 break-all">
                                <strong>Feedback:</strong> {submission.reviewer_feedback}
                              </p>
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-400 mt-2">
                            {submission.submitted_at ? `Submitted ${new Date(submission.submitted_at).toLocaleDateString()}` : 
                             submission.created_at ? `Created ${new Date(submission.created_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-6 p-4 bg-purple-50 rounded-xl">
                  <p className="text-sm text-purple-700">
                    <strong>How it works:</strong> When you add a look and tag a brand, it's sent for their approval. 
                    Once approved, the content appears on both your profile and the brand's profile as "Collaboration Content."
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-6" data-testid="campaigns-section">
            {campaignLoading && !selectedCampaign ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
              </div>
            ) : user.user_type === 'brand' ? (
              /* Brand View - Manage Campaigns */
              <div>
                {/* Payment Message Banner */}
                {paymentMessage && (
                  <div className={`mb-4 p-4 rounded-xl ${
                    paymentMessage.type === 'success' ? 'bg-green-100 text-green-700' :
                    paymentMessage.type === 'error' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span>{paymentMessage.text}</span>
                      <button onClick={() => setPaymentMessage(null)} className="text-current opacity-50 hover:opacity-100">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Wallet Balance Banner */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm">Campaign Wallet Balance</p>
                      <p className="text-4xl font-bold">${walletData?.wallet?.balance?.toFixed(2) || '0.00'}</p>
                    </div>
                    <button
                      onClick={() => setShowDepositModal(true)}
                      className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl font-medium transition-colors"
                      data-testid="deposit-wallet-btn"
                    >
                      <Plus className="w-5 h-5 inline mr-2" />
                      Add Funds
                    </button>
                  </div>
                  <p className="text-emerald-100 text-sm mt-2">
                    Funds are used to pay influencers when they create approved content for your campaigns.
                  </p>
                </div>

                {/* Header with Create Campaign Button */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Your Campaigns</h2>
                    <p className="text-sm text-gray-500">Create campaigns to hire influencers for guaranteed content</p>
                  </div>
                  <button
                    onClick={() => setShowCreateCampaignModal(true)}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg"
                    data-testid="create-campaign-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Campaign
                  </button>
                </div>
                
                {/* Campaign Stats Overview */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Active Campaigns</p>
                    <p className="text-2xl font-bold text-gray-800">{campaigns.filter(c => c.status === 'active').length}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Total Spent</p>
                    <p className="text-2xl font-bold text-gray-800">${campaigns.reduce((sum, c) => sum + (c.budget_spent || 0), 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Content Created</p>
                    <p className="text-2xl font-bold text-gray-800">{campaigns.reduce((sum, c) => sum + (c.completed_content || 0), 0)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Active Influencers</p>
                    <p className="text-2xl font-bold text-gray-800">{campaigns.reduce((sum, c) => sum + (c.accepted_influencers || 0), 0)}</p>
                  </div>
                </div>

                {/* Campaign Detail View */}
                {selectedCampaign ? (
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <button 
                          onClick={() => setSelectedCampaign(null)}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg mb-3 flex items-center gap-2 font-medium transition-colors"
                          data-testid="back-to-campaigns"
                        >
                          <ChevronRight className="w-4 h-4 rotate-180" />
                          Back to Campaigns
                        </button>
                        <h3 className="text-xl font-bold text-gray-800">{selectedCampaign.name}</h3>
                        <p className="text-gray-500">{selectedCampaign.product_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">${selectedCampaign.fee_per_content}</p>
                        <p className="text-sm text-gray-500">per content</p>
                      </div>
                    </div>

                    {/* Campaign Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-800">{selectedCampaign.accepted_influencers || 0}/{selectedCampaign.max_influencers}</p>
                        <p className="text-xs text-gray-500">Influencers</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-800">{selectedCampaign.completed_content || 0}</p>
                        <p className="text-xs text-gray-500">Content Created</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-emerald-600">${selectedCampaign.budget_remaining?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-gray-500">Budget Left</p>
                      </div>
                    </div>

                    {/* Applications */}
                    <h4 className="font-semibold text-gray-800 mb-4">
                      Applications ({selectedCampaign.applications?.length || 0})
                    </h4>
                    {!selectedCampaign.applications || selectedCampaign.applications.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No applications yet. Influencers will apply soon!</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedCampaign.applications.map(app => (
                          <div key={app.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl" data-testid={`application-${app.id}`}>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-orange-400 rounded-full flex items-center justify-center text-white font-medium">
                                {app.influencer_name?.charAt(0) || 'I'}
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">@{app.influencer_handle}</p>
                                <p className="text-sm text-gray-500">{app.influencer_name}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {app.status === 'pending' ? (
                                <>
                                  <button
                                    onClick={() => acceptApplication(selectedCampaign.id, app.id)}
                                    disabled={processingApplication === app.id}
                                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 text-sm"
                                  >
                                    {processingApplication === app.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accept'}
                                  </button>
                                  <button
                                    onClick={() => rejectApplication(selectedCampaign.id, app.id)}
                                    disabled={processingApplication === app.id}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 text-sm"
                                  >
                                    Reject
                                  </button>
                                </>
                              ) : (
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  app.status === 'accepted' 
                                    ? app.content_approved ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {app.content_approved ? 'Content Approved' : app.status === 'accepted' ? 'Accepted - Awaiting Content' : 'Rejected'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Campaigns List */
                  campaigns.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                      <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No Campaigns Yet</h3>
                      <p className="text-gray-500 mb-4">
                        Create a campaign to hire influencers for guaranteed content. You pay a fixed fee per approved content piece.
                      </p>
                      <button
                        onClick={() => setShowCreateCampaignModal(true)}
                        className="px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg"
                      >
                        Create Your First Campaign
                      </button>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-5">
                      {campaigns.map(campaign => {
                        const pendingCount = (campaign.applications || []).filter(a => a.status === 'pending').length;
                        const progressPct = campaign.max_influencers > 0 
                          ? Math.round(((campaign.accepted_influencers || 0) / campaign.max_influencers) * 100) 
                          : 0;
                        return (
                        <div 
                          key={campaign.id} 
                          className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-rose-200 hover:-translate-y-1"
                          onClick={() => fetchCampaignDetails(campaign.id)}
                          data-testid={`campaign-card-${campaign.id}`}
                        >
                          {/* Card Header with gradient */}
                          <div className={`px-5 py-3 ${
                            campaign.status === 'active' 
                              ? 'bg-gradient-to-r from-rose-500/10 to-orange-500/10' 
                              : 'bg-gray-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                                campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                              }`}>
                                {campaign.status === 'active' ? 'Active' : campaign.status}
                              </span>
                              {pendingCount > 0 && (
                                <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-rose-500 text-white animate-pulse">
                                  {pendingCount} new {pendingCount === 1 ? 'applicant' : 'applicants'}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Card Body */}
                          <div className="px-5 py-4">
                            <h4 className="font-bold text-gray-800 text-lg mb-1 group-hover:text-rose-600 transition-colors">{campaign.name}</h4>
                            <p className="text-sm text-gray-500 mb-4">{campaign.product_name}</p>

                            {/* Stats Row */}
                            <div className="grid grid-cols-3 gap-3 mb-4">
                              <div className="text-center bg-gray-50 rounded-lg py-2">
                                <p className="text-lg font-bold text-green-600">${campaign.fee_per_content}</p>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Per Content</p>
                              </div>
                              <div className="text-center bg-gray-50 rounded-lg py-2">
                                <p className="text-lg font-bold text-gray-800">{campaign.accepted_influencers || 0}<span className="text-gray-400">/{campaign.max_influencers}</span></p>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Influencers</p>
                              </div>
                              <div className="text-center bg-gray-50 rounded-lg py-2">
                                <p className="text-lg font-bold text-gray-800">{campaign.completed_content || 0}</p>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Content</p>
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                <span>Influencer slots</span>
                                <span>{progressPct}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-rose-500 to-orange-400 rounded-full transition-all"
                                  style={{ width: `${Math.min(progressPct, 100)}%` }}
                                />
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <span className="text-xs text-gray-400">
                                Budget: <span className="font-medium text-gray-600">${campaign.budget_remaining?.toFixed(0) || '0'} left</span>
                              </span>
                              <span className="text-xs text-rose-500 font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                                View Details <ArrowRight className="w-3 h-3" />
                              </span>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )
                )}

                {/* How it Works */}
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <strong>How it works:</strong> Create a campaign with a guaranteed fee per content. Influencers apply, you accept them, 
                    they create content, you approve it, and they automatically get paid from your wallet. No more trust issues!
                  </p>
                </div>
              </div>
            ) : user.user_type === 'influencer' ? (
              /* Influencer View - Browse Campaigns & Track Earnings */
              <div>
                {/* Payment Message Banner */}
                {paymentMessage && (
                  <div className={`mb-4 p-4 rounded-xl ${
                    paymentMessage.type === 'success' ? 'bg-green-100 text-green-700' :
                    paymentMessage.type === 'error' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span>{paymentMessage.text}</span>
                      <button onClick={() => setPaymentMessage(null)} className="text-current opacity-50 hover:opacity-100">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Earnings Summary Banner */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100">Available Balance</p>
                      <p className="text-4xl font-bold">${walletData?.earnings?.available_balance?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-100">Total Earned</p>
                      <p className="text-2xl font-semibold">${walletData?.earnings?.total_earned?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 mt-4 pt-4 border-t border-green-400">
                    <div>
                      <p className="text-green-100 text-sm">Total Withdrawn</p>
                      <p className="text-xl font-semibold">${walletData?.earnings?.total_withdrawn?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-green-100 text-sm">Active Applications</p>
                      <p className="text-xl font-semibold">{myApplications.filter(a => a.status === 'accepted' || a.status === 'pending').length}</p>
                    </div>
                    <div className="ml-auto">
                      <button
                        onClick={() => setShowWithdrawModal(true)}
                        disabled={!walletData?.earnings?.available_balance || walletData.earnings.available_balance < 10}
                        className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors disabled:opacity-50"
                        data-testid="withdraw-btn"
                      >
                        Withdraw Funds
                      </button>
                    </div>
                  </div>
                </div>

                {/* Stripe Connect Status */}
                <div className={`mb-6 p-4 rounded-xl ${
                  connectStatus?.status === 'active' ? 'bg-green-50 border border-green-200' :
                  connectStatus?.status === 'pending' ? 'bg-amber-50 border border-amber-200' :
                  'bg-gray-50 border border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-800">
                        {connectStatus?.status === 'active' ? '✅ Bank Account Connected' :
                         connectStatus?.status === 'pending' ? '⏳ Account Under Review' :
                         connectStatus?.status === 'incomplete' ? '⚠️ Setup Incomplete' :
                         '🏦 Connect Your Bank Account'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {connectStatus?.message || 'Connect your bank to receive payouts from campaigns'}
                      </p>
                    </div>
                    {connectStatus?.status === 'active' ? (
                      <button
                        onClick={openConnectDashboard}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                        data-testid="connect-dashboard-btn"
                      >
                        View Stripe Dashboard
                      </button>
                    ) : (
                      <button
                        onClick={startConnectOnboarding}
                        disabled={processingPayment}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 text-sm disabled:opacity-50"
                        data-testid="connect-bank-btn"
                      >
                        {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                         connectStatus?.status === 'incomplete' ? 'Complete Setup' : 'Connect Bank Account'}
                      </button>
                    )}
                  </div>
                </div>

                {/* My Applications Section */}
                {myApplications.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">My Applications</h2>
                    <div className="space-y-3">
                      {myApplications.map(app => (
                        <div key={app.id} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`my-application-${app.id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-800">{app.campaign_name || app.campaign?.name}</h4>
                              <p className="text-sm text-gray-500">@{app.brand_handle || app.campaign?.brand_handle} • ${app.fee_per_content || app.campaign?.fee_per_content}/content</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {app.status === 'pending' && (
                                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center">
                                  <Clock className="w-3 h-3 mr-1" /> Pending Review
                                </span>
                              )}
                              {app.status === 'accepted' && (!app.content_submitted || app.content_rejected) && !app.content_approved && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setShowSubmitContentModal({...app, campaign_name: app.campaign_name || app.campaign?.name, fee_per_content: app.fee_per_content || app.campaign?.fee_per_content, campaign_id: app.campaign_id || app.campaign?.id}); }}
                                  className="px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg text-sm hover:shadow-lg"
                                  data-testid={`submit-content-${app.id}`}
                                >
                                  {app.content_rejected ? 'Resubmit Content' : 'Submit Content'}
                                </button>
                              )}
                              {app.content_rejected && app.reviewer_feedback && (
                                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center" data-testid={`rejected-badge-${app.id}`}>
                                  <XCircle className="w-3 h-3 mr-1" /> Needs Revision
                                </span>
                              )}
                              {app.status === 'accepted' && app.content_submitted && !app.content_approved && !app.content_rejected && (
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                  Content Under Review
                                </span>
                              )}
                              {app.content_approved && (
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Paid ${app.fee_per_content || app.campaign?.fee_per_content}
                                </span>
                              )}
                              {app.status === 'rejected' && (
                                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                  Not Selected
                                </span>
                              )}
                            </div>
                          </div>
                          {app.content_rejected && app.reviewer_feedback && (
                            <div className="px-4 pb-3">
                              <div className="p-2 bg-red-50 rounded-lg">
                                <p className="text-xs text-red-600 break-all" data-testid={`rejection-feedback-${app.id}`}>
                                  <strong>Brand feedback:</strong> {app.reviewer_feedback}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Campaigns */}
                <h2 className="text-xl font-bold text-gray-800 mb-4">Available Campaigns</h2>
                <p className="text-gray-500 mb-6">Apply to campaigns and earn guaranteed payments for your content!</p>
                
                {campaigns.filter(c => !myApplications.find(a => a.campaign_id === c.id)).length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No New Campaigns</h3>
                    <p className="text-gray-500">
                      {myApplications.length > 0 
                        ? "You've applied to all available campaigns. Check back soon for new opportunities!"
                        : "No campaigns available right now. Brands will post new opportunities soon!"}
                    </p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {campaigns
                      .filter(c => !myApplications.find(a => a.campaign_id === c.id))
                      .map(campaign => (
                        <div key={campaign.id} className="bg-white rounded-xl p-5 shadow-sm" data-testid={`available-campaign-${campaign.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">@{campaign.brand_handle}</p>
                              <h4 className="font-semibold text-gray-800">{campaign.name}</h4>
                              <p className="text-sm text-gray-500">{campaign.product_name}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-600">${campaign.fee_per_content}</p>
                              <p className="text-xs text-gray-400">per content</p>
                            </div>
                          </div>
                          
                          {campaign.requirements && (
                            <p className="text-sm text-gray-600 mb-3 p-2 bg-gray-50 rounded-lg">
                              {campaign.requirements}
                            </p>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400">
                              {campaign.max_influencers - (campaign.accepted_influencers || 0)} spots left
                            </span>
                            <button
                              onClick={() => applyToCampaign(campaign.id)}
                              disabled={applyingToCampaign === campaign.id}
                              className="px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:shadow-lg disabled:opacity-50"
                            >
                              {applyingToCampaign === campaign.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Apply Now'
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {/* Recent Payments */}
                {walletData?.transactions?.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Recent Payments</h3>
                    <div className="bg-white rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Campaign</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {walletData.transactions.slice(0, 5).map(tx => (
                            <tr key={tx.id}>
                              <td className="px-4 py-3 text-sm text-gray-800 max-w-xs truncate">{tx.description}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {new Date(tx.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-green-600 font-medium text-right">
                                +${tx.amount?.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="mt-6 p-4 bg-purple-50 rounded-xl">
                  <p className="text-sm text-purple-700">
                    <strong>Guaranteed Payments:</strong> When you apply and get accepted to a campaign, create content for the brand. 
                    Once approved, you automatically receive the guaranteed fee - no tracking links, no uncertainty!
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Create Campaign Modal (Brand) */}
        {showCreateCampaignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Create New Campaign</h3>
                <button onClick={() => setShowCreateCampaignModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Summer Collection Launch"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                    data-testid="campaign-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    value={campaignForm.product_name}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, product_name: e.target.value }))}
                    placeholder="Urban Hoodie Black"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                    data-testid="campaign-product-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product URL</label>
                  <input
                    type="url"
                    value={campaignForm.product_url}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, product_url: e.target.value }))}
                    placeholder="https://yourstore.com/product/..."
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fee Per Content ($)</label>
                    <input
                      type="number"
                      min="5"
                      value={campaignForm.fee_per_content}
                      onChange={(e) => setCampaignForm(prev => ({ ...prev, fee_per_content: Number(e.target.value) }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                      data-testid="campaign-fee-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">Min $5 per content</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Influencers</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={campaignForm.max_influencers}
                      onChange={(e) => setCampaignForm(prev => ({ ...prev, max_influencers: Number(e.target.value) }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                      data-testid="campaign-slots-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Requirements (Optional)</label>
                  <textarea
                    value={campaignForm.requirements}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, requirements: e.target.value }))}
                    placeholder="Describe what kind of content you're looking for..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                  />
                </div>

                {/* Budget Preview */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm font-medium text-gray-700 mb-2">Budget Summary</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Content Fees ({campaignForm.max_influencers} × ${campaignForm.fee_per_content})</span>
                      <span>${(campaignForm.max_influencers * campaignForm.fee_per_content).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Platform Fee (15%)</span>
                      <span>${(campaignForm.max_influencers * campaignForm.fee_per_content * 0.15).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold pt-2 border-t border-gray-200">
                      <span>Total Budget Required</span>
                      <span className="text-rose-500">${(campaignForm.max_influencers * campaignForm.fee_per_content * 1.15).toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Your wallet: ${walletData?.wallet?.balance?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={createCampaign}
                  disabled={!campaignForm.name || !campaignForm.product_name || (walletData?.wallet?.balance || 0) < (campaignForm.max_influencers * campaignForm.fee_per_content * 1.15)}
                  className="w-full py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50"
                  data-testid="create-campaign-submit"
                >
                  {(walletData?.wallet?.balance || 0) < (campaignForm.max_influencers * campaignForm.fee_per_content * 1.15)
                    ? 'Insufficient Wallet Balance'
                    : 'Create Campaign'
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deposit Modal (Brand) */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Add Funds to Wallet</h3>
                <button onClick={() => setShowDepositModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input
                    type="number"
                    min="10"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(Number(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500 text-lg font-semibold"
                    data-testid="deposit-amount-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Minimum deposit: $10</p>
                </div>
                
                {/* Quick amounts */}
                <div className="flex gap-2">
                  {[50, 100, 250, 500].map(amt => (
                    <button
                      key={amt}
                      onClick={() => setDepositAmount(amt)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        depositAmount === amt 
                          ? 'bg-rose-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      ${amt}
                    </button>
                  ))}
                </div>

                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <strong>Secure Payment:</strong> You'll be redirected to Stripe to complete your payment securely.
                  </p>
                </div>
                
                <button
                  onClick={depositToWallet}
                  disabled={depositAmount < 10 || processingPayment}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50 flex items-center justify-center"
                  data-testid="deposit-submit-btn"
                >
                  {processingPayment ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>Pay ${depositAmount} with Stripe</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submit Content Modal (Influencer) */}
        {showSubmitContentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Submit Campaign Content</h3>
                <button onClick={() => { setShowSubmitContentModal(null); setContentSubmission({ image_base64: '', caption: '' }); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm"><strong>Campaign:</strong> {showSubmitContentModal.campaign_name}</p>
                  <p className="text-sm text-green-600"><strong>Fee:</strong> ${showSubmitContentModal.fee_per_content}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content Image</label>
                  <label className="block">
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      contentSubmission.image_base64 ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-rose-300'
                    }`}>
                      {contentSubmission.image_base64 ? (
                        <img 
                          src={`data:image/jpeg;base64,${contentSubmission.image_base64}`} 
                          alt="Content preview" 
                          className="max-h-48 mx-auto rounded-lg"
                        />
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">Upload your content</p>
                          <p className="text-xs text-gray-400">PNG, JPG up to 10MB</p>
                        </>
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={handleContentImageUpload} className="hidden" />
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Caption (Optional)</label>
                  <textarea
                    value={contentSubmission.caption}
                    onChange={(e) => setContentSubmission(prev => ({ ...prev, caption: e.target.value }))}
                    placeholder="Add a caption for your content..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                  />
                </div>
                
                <button
                  onClick={() => submitCampaignContent(showSubmitContentModal.campaign_id, showSubmitContentModal)}
                  disabled={!contentSubmission.image_base64 || campaignLoading}
                  className="w-full py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50"
                >
                  {campaignLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Submit for Approval'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Withdraw Modal (Influencer) */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Withdraw Funds</h3>
                <button onClick={() => setShowWithdrawModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {!connectStatus?.payouts_enabled ? (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-4">You need to connect your bank account before withdrawing.</p>
                  <button
                    onClick={startConnectOnboarding}
                    className="px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600"
                  >
                    Connect Bank Account
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-gray-500">Available Balance</p>
                    <p className="text-2xl font-bold text-green-600">${walletData?.earnings?.available_balance?.toFixed(2) || '0.00'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Withdrawal Amount ($)</label>
                    <input
                      type="number"
                      min="10"
                      max={walletData?.earnings?.available_balance || 0}
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(Number(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 text-lg font-semibold"
                      data-testid="withdraw-amount-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">Minimum withdrawal: $10</p>
                  </div>
                  
                  {/* Quick amounts */}
                  <div className="flex gap-2">
                    {[50, 100, 250].filter(amt => amt <= (walletData?.earnings?.available_balance || 0)).map(amt => (
                      <button
                        key={amt}
                        onClick={() => setWithdrawAmount(amt)}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          withdrawAmount === amt 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        ${amt}
                      </button>
                    ))}
                    <button
                      onClick={() => setWithdrawAmount(walletData?.earnings?.available_balance || 0)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        withdrawAmount === walletData?.earnings?.available_balance 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      All
                    </button>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm text-blue-700">
                      <strong>Processing:</strong> Withdrawals typically arrive in 2-3 business days via Stripe.
                    </p>
                  </div>
                  
                  <button
                    onClick={requestWithdrawal}
                    disabled={withdrawAmount < 10 || withdrawAmount > (walletData?.earnings?.available_balance || 0) || processingPayment}
                    className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50 flex items-center justify-center"
                    data-testid="withdraw-submit-btn"
                  >
                    {processingPayment ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>Withdraw ${withdrawAmount}</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6" data-testid="analytics-section">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
              </div>
            ) : analyticsData ? (
              <>
                {/* Usage Overview */}
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm text-gray-500">Images This Month</h4>
                      <BarChart3 className="w-5 h-5 text-rose-400" />
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{analyticsData.usage?.images_this_month || 0}</p>
                    <div className="mt-2 bg-gray-100 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-rose-500 to-orange-500 rounded-full h-2"
                        style={{ width: `${Math.min(analyticsData.usage?.usage_percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{analyticsData.usage?.usage_percentage}% of {analyticsData.usage?.images_limit} limit</p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm text-gray-500">Last 7 Days</h4>
                      <TrendingUp className="w-5 h-5 text-green-400" />
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{analyticsData.usage?.images_7d || 0}</p>
                    <p className="text-xs text-gray-400 mt-1">images generated</p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm text-gray-500">Last 30 Days</h4>
                      <PieChart className="w-5 h-5 text-blue-400" />
                    </div>
                    <p className="text-3xl font-bold text-gray-800">{analyticsData.usage?.images_30d || 0}</p>
                    <p className="text-xs text-gray-400 mt-1">images generated</p>
                  </div>
                  
                  {user.user_type === 'brand' && analyticsData.brand && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm text-gray-500">Collaborations</h4>
                        <Users className="w-5 h-5 text-purple-400" />
                      </div>
                      <p className="text-3xl font-bold text-gray-800">
                        {(analyticsData.brand.collaborations?.active || 0) + (analyticsData.brand.collaborations?.pending || 0)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {analyticsData.brand.collaborations?.active || 0} active · {analyticsData.brand.collaborations?.pending || 0} pending
                      </p>
                    </div>
                  )}
                  
                  {user.user_type === 'influencer' && analyticsData.influencer && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm text-gray-500">Campaign Earnings</h4>
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <p className="text-3xl font-bold text-gray-800">${analyticsData.influencer.campaign_earnings?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs text-gray-400 mt-1">from approved content</p>
                    </div>
                  )}
                </div>

                {/* Daily Usage Chart */}
                {analyticsData.usage?.daily_breakdown && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Daily Usage (Last 7 Days)</h4>
                    {analyticsData.usage.daily_breakdown.every(d => d.count === 0) ? (
                      <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                        <BarChart3 className="w-8 h-8 mb-2 opacity-40" />
                        <p className="text-sm">No try-ons this week. Generate some to see your activity!</p>
                      </div>
                    ) : (
                      <div className="flex items-end justify-between h-32 gap-2">
                        {analyticsData.usage.daily_breakdown.map((day, idx) => {
                          const maxCount = Math.max(...analyticsData.usage.daily_breakdown.map(d => d.count), 1);
                          const height = (day.count / maxCount) * 100;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center">
                              <div 
                                className="w-full bg-gradient-to-t from-rose-500 to-orange-400 rounded-t-lg transition-all"
                                style={{ height: `${Math.max(height, 5)}%` }}
                              ></div>
                              <span className="text-xs text-gray-400 mt-2">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                              <span className="text-xs font-medium text-gray-600">{day.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Brand ROI Section (Advanced Analytics) */}
                {user.user_type === 'brand' && analyticsData.has_advanced_analytics && analyticsData.roi && analyticsData.roi.roi_percentage !== undefined && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800">ROI & Savings Analysis</h4>
                        <p className="text-sm text-gray-500">Based on your VirtualFit usage</p>
                      </div>
                      <div className={`px-4 py-2 rounded-full ${analyticsData.roi.is_positive_roi ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} font-bold flex items-center`}>
                        {analyticsData.roi.is_positive_roi ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
                        {analyticsData.roi.roi_percentage}% ROI
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Returns Prevented</p>
                        <p className="text-2xl font-bold text-gray-800">{analyticsData.roi.estimated_returns_prevented}</p>
                        <p className="text-sm text-green-600">~${analyticsData.roi.estimated_return_savings} saved</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Shipping Savings</p>
                        <p className="text-2xl font-bold text-gray-800">${analyticsData.roi.estimated_shipping_savings}</p>
                        <p className="text-sm text-green-600">reduced costs</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Est. Conversions</p>
                        <p className="text-2xl font-bold text-gray-800">{analyticsData.roi.estimated_conversions}</p>
                        <p className="text-sm text-gray-400">{analyticsData.roi.conversion_rate} rate</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Revenue Influenced</p>
                        <p className="text-2xl font-bold text-green-600">${analyticsData.roi.estimated_revenue_influenced}</p>
                        <p className="text-sm text-gray-400">@ ${analyticsData.roi.avg_order_value} AOV</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-green-200 flex items-center justify-between">
                      <div>
                        <span className="text-sm text-gray-500">Subscription Cost:</span>
                        <span className="ml-2 font-semibold">${analyticsData.roi.subscription_cost}/mo</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Total Savings:</span>
                        <span className="ml-2 font-bold text-green-600">${analyticsData.roi.total_estimated_savings}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Brand Campaign ROI Section */}
                {user.user_type === 'brand' && analyticsData.campaign_roi && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100" data-testid="brand-campaign-roi">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800">Campaign ROI</h4>
                        <p className="text-sm text-gray-500">Performance from influencer campaigns</p>
                      </div>
                      <div className={`px-4 py-2 rounded-full ${analyticsData.campaign_roi.campaign_roi_percentage > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'} font-bold flex items-center`} data-testid="campaign-roi-badge">
                        {analyticsData.campaign_roi.campaign_roi_percentage > 0 ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
                        {analyticsData.campaign_roi.campaign_roi_percentage}% Campaign ROI
                      </div>
                    </div>
                    <div className="grid md:grid-cols-4 gap-4">
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Total Campaigns</p>
                        <p className="text-2xl font-bold text-gray-800" data-testid="total-campaigns">{analyticsData.campaign_roi.total_campaigns}</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Total Spent</p>
                        <p className="text-2xl font-bold text-gray-800" data-testid="total-campaign-spent">${analyticsData.campaign_roi.total_spent?.toFixed(2)}</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Content Received</p>
                        <p className="text-2xl font-bold text-gray-800" data-testid="content-received">{analyticsData.campaign_roi.content_received}</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Est. Value/Content</p>
                        <p className="text-2xl font-bold text-blue-600" data-testid="value-per-content">${analyticsData.campaign_roi.estimated_value_per_content}</p>
                      </div>
                    </div>
                    {analyticsData.roi && (
                      <div className="mt-4 pt-4 border-t border-blue-200 flex items-center justify-between">
                        <div>
                          <span className="text-sm text-gray-500">Combined ROI (Try-On + Campaigns):</span>
                          <span className={`ml-2 font-bold ${analyticsData.roi.combined_roi_percentage > 0 ? 'text-green-600' : 'text-red-500'}`} data-testid="combined-roi">
                            {analyticsData.roi.combined_roi_percentage}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Influencer Campaign Earnings Section */}
                {user.user_type === 'influencer' && analyticsData.influencer && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Campaign Performance</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Total Earnings</p>
                        <p className="text-2xl font-bold text-green-600">${analyticsData.influencer.campaign_earnings?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-gray-400">From approved content</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Content Approved</p>
                        <p className="text-2xl font-bold text-gray-800">{analyticsData.influencer.content_approved || 0}</p>
                        <p className="text-xs text-gray-400">Paid submissions</p>
                      </div>
                      <div className="bg-white/70 rounded-xl p-4">
                        <p className="text-sm text-gray-500">Active Campaigns</p>
                        <p className="text-2xl font-bold text-gray-800">{analyticsData.influencer.active_campaigns || 0}</p>
                        <p className="text-xs text-gray-400">Accepted to work</p>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-white/50 rounded-xl">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Guaranteed Fees:</span> You earn a guaranteed fee when brands approve your content submissions.
                        Withdraw your earnings via Stripe Connect from the Dashboard.
                      </p>
                    </div>
                  </div>
                )}

                {/* Pro Influencer Advanced Analytics */}
                {user.user_type === 'influencer' && analyticsData.has_advanced_analytics && analyticsData.influencer && (
                  <>
                    {/* Content Pipeline & Approval Rate */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="pro-content-pipeline">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">Content Pipeline</h4>
                      <div className="grid md:grid-cols-4 gap-4">
                        <div className="bg-green-50 rounded-xl p-4 text-center">
                          <p className="text-sm text-gray-500">Approved</p>
                          <p className="text-2xl font-bold text-green-600">{analyticsData.influencer.content_approved || 0}</p>
                        </div>
                        <div className="bg-amber-50 rounded-xl p-4 text-center">
                          <p className="text-sm text-gray-500">Pending Review</p>
                          <p className="text-2xl font-bold text-amber-600">{analyticsData.influencer.content_stats?.pending || 0}</p>
                        </div>
                        <div className="bg-red-50 rounded-xl p-4 text-center">
                          <p className="text-sm text-gray-500">Rejected</p>
                          <p className="text-2xl font-bold text-red-500">{analyticsData.influencer.content_stats?.rejected || 0}</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4 text-center">
                          <p className="text-sm text-gray-500">Approval Rate</p>
                          <p className="text-2xl font-bold text-blue-600">{analyticsData.influencer.content_stats?.approval_rate || '100%'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Profile & Earnings Insights */}
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100" data-testid="pro-earnings-insights">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">Pro Insights</h4>
                      <div className="grid md:grid-cols-4 gap-4">
                        <div className="bg-white/70 rounded-xl p-4">
                          <p className="text-sm text-gray-500">Profile Visits</p>
                          <p className="text-2xl font-bold text-purple-600">{analyticsData.influencer.profile_visits || 0}</p>
                          <p className="text-xs text-gray-400">Total views</p>
                        </div>
                        <div className="bg-white/70 rounded-xl p-4">
                          <p className="text-sm text-gray-500">Portfolio Items</p>
                          <p className="text-2xl font-bold text-gray-800">{analyticsData.influencer.portfolio_count || 0}</p>
                          <p className="text-xs text-gray-400">Published looks</p>
                        </div>
                        <div className="bg-white/70 rounded-xl p-4">
                          <p className="text-sm text-gray-500">Earnings (30d)</p>
                          <p className="text-2xl font-bold text-green-600">${analyticsData.influencer.earnings_30d?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-gray-400">Last 30 days</p>
                        </div>
                        <div className="bg-white/70 rounded-xl p-4">
                          <p className="text-sm text-gray-500">Avg per Content</p>
                          <p className="text-2xl font-bold text-gray-800">${analyticsData.influencer.avg_earning_per_content?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-gray-400">Per approved piece</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Upgrade Prompt for Basic Plans - Only show for non-Pro users */}
                {!analyticsData.has_advanced_analytics && !isProPlan && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100 text-center">
                    <Crown className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Unlock Advanced Analytics</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Upgrade to {user.user_type === 'brand' ? 'Brand Enterprise' : 'Influencer Pro'} for detailed ROI tracking, 
                      conversion analytics, and more insights.
                    </p>
                    <Link
                      to="/subscription"
                      className="inline-block px-6 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg"
                    >
                      Upgrade Now
                    </Link>
                  </div>
                )}
              </>
            ) : (
              // Fallback basic analytics
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h4 className="text-sm text-gray-500 mb-1">Images Generated</h4>
                  <p className="text-3xl font-bold text-gray-800">{user.images_used || 0}</p>
                  <p className="text-sm text-gray-400">of {user.images_limit} this month</p>
                </div>
                
                {user.user_type === 'brand' && (
                  <>
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <h4 className="text-sm text-gray-500 mb-1">Est. Returns Saved</h4>
                      <p className="text-3xl font-bold text-green-500">
                        ${((user.images_used || 0) * 15 * 0.4).toFixed(0)}
                      </p>
                      <p className="text-sm text-gray-400">40% reduction</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <h4 className="text-sm text-gray-500 mb-1">Est. Shipping Saved</h4>
                      <p className="text-3xl font-bold text-green-500">
                        ${((user.images_used || 0) * 8 * 0.25).toFixed(0)}
                      </p>
                      <p className="text-sm text-gray-400">25% savings</p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Footer */}
      <Footer isAuthenticated={true} isProPlan={isProPlan} />
    </div>
  );
};

export default Dashboard;
